import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

console.log('[API] Backend URL:', BACKEND_URL);
console.log('[API] API Base:', API_BASE);

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('boat360_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  console.log('[API] Request:', config.method?.toUpperCase(), config.url);
  return config;
}, (error) => {
  console.error('[API] Request error:', error);
  return Promise.reject(error);
});

// Handle auth errors
api.interceptors.response.use(
  (response) => {
    console.log('[API] Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('[API] Response error:', error.message);
    console.error('[API] Error details:', {
      url: error.config?.url,
      status: error.response?.status,
      data: error.response?.data,
    });
    if (error.response?.status === 401) {
      localStorage.removeItem('boat360_token');
      localStorage.removeItem('boat360_user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateMe: (data) => api.put('/auth/me', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  deleteAccount: () => api.delete('/profile'),
};

// Location APIs
export const locationAPI = {
  update: (data) => api.post('/location/update', data),
  getMyLocation: () => api.get('/location/me'),
  getPublicBoaters: (params) => api.get('/map/public', { params }),
  getFriendsLocations: () => api.get('/map/friends'),
};

// Friends APIs
export const friendsAPI = {
  sendRequest: (data) => api.post('/friends/request', data),
  respondToRequest: (data) => api.post('/friends/respond', data),
  getRequests: () => api.get('/friends/requests'),
  getList: () => api.get('/friends/list'),
  updatePermissions: (friendId, data) => api.put(`/friends/${friendId}/permissions`, data),
  remove: (friendId) => api.delete(`/friends/${friendId}`),
};

// Trips APIs
export const tripsAPI = {
  start: (data) => api.post('/trips/start', data || {}),
  end: () => api.post('/trips/end'),
  getActive: () => api.get('/trips/active'),
  getHistory: (limit = 10) => api.get('/trips/history', { params: { limit } }),
  getRoute: (tripId) => api.get(`/trips/${tripId}/route`),
};

// Saved Routes APIs
export const routesAPI = {
  save: (data) => api.post('/routes/save', data),
  getMine: () => api.get('/routes/mine'),
  getSharedWithMe: () => api.get('/routes/shared-with-me'),
  getPublic: (lat, lon, radiusNm = 50) => api.get('/routes/public', { params: { lat, lon, radius_nm: radiusNm } }),
  getDetails: (routeId) => api.get(`/routes/${routeId}`),
  share: (routeId, friendUserId) => api.post(`/routes/${routeId}/share`, { friend_user_id: friendUserId }),
  delete: (routeId) => api.delete(`/routes/${routeId}`),
};

// Saved Points (Waypoints) APIs - Boat 360 Plus
export const pointsAPI = {
  create: (data) => api.post('/points', data),
  getMine: () => api.get('/points/mine'),
  share: (pointId, friendUserId) => api.post(`/points/${pointId}/share`, { friend_user_id: friendUserId }),
  delete: (pointId) => api.delete(`/points/${pointId}`),
};

// Reports APIs
export const reportsAPI = {
  create: (data) => api.post('/reports/create', data),
  vote: (data) => api.post('/reports/vote', data),
  getNearby: (lat, lon, radius = 10) => api.get('/reports/nearby', { params: { lat, lon, radius } }),
  getMine: () => api.get('/reports/mine'),
};

// Alerts APIs
export const alertsAPI = {
  send: (data) => api.post('/alerts/send', data),
  getReceived: (unreadOnly = false) => api.get('/alerts/received', { params: { unread_only: unreadOnly } }),
  markRead: (alertId) => api.post(`/alerts/${alertId}/read`),
  clearAll: () => api.post('/alerts/clear-all'),
};

// SOS APIs (Boat 360 Freemium)
export const sosAPI = {
  trigger: (data) => api.post('/sos/trigger', data),
  cancel: (sosId, sosType) => api.post(`/sos/cancel?sos_id=${sosId}&sos_type=${sosType}`),
  getActive: () => api.get('/sos/active'),
  getNearby: (lat, lon) => api.get('/sos/nearby', { params: { lat, lon } }),
  getFriendsSOS: () => api.get('/sos/friends'),
  acknowledge: (data) => api.post('/sos/acknowledge', data),
  getHelpers: () => api.get('/sos/helpers'),
};

// Emergency Marker APIs (MOB - Man Overboard)
export const emergencyMarkerAPI = {
  create: (data) => api.post('/markers/emergency', data),
  getAll: () => api.get('/markers/emergency'),
  clear: (markerId) => api.delete(`/markers/emergency/${markerId}`),
};

// Subscription APIs (Boat 360 Freemium)
export const subscriptionAPI = {
  getStatus: () => api.get('/subscription/status'),
  upgrade: (data) => api.post('/subscription/upgrade', data || {}),
  verify: (data) => api.post('/subscription/verify', data || {}),
  downgrade: () => api.post('/subscription/downgrade'),
};

// Profile Photo APIs (Boat 360 Plus)
export const profileAPI = {
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/profile/photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deletePhoto: () => api.delete('/profile/photo'),
  getPhoto: () => api.get('/profile/photo'),
};

// Admin APIs
export const adminAPI = {
  // Dashboard
  getDashboard: () => api.get('/admin/dashboard', { timeout: 10000 }),
  
  // Users
  getUsers: (params) => api.get('/admin/users', { params, timeout: 10000 }),
  getUserDetail: (userId) => api.get(`/admin/users/${userId}`, { timeout: 10000 }),
  suspendUser: (data) => api.post('/admin/users/suspend', data, { timeout: 10000 }),
  banUser: (data) => api.post('/admin/users/ban', data, { timeout: 10000 }),
  unbanUser: (userId) => api.post(`/admin/users/${userId}/unban`, {}, { timeout: 10000 }),
  forceInvisible: (userId) => api.post(`/admin/users/${userId}/force-invisible`, {}, { timeout: 10000 }),
  resetAbuseScore: (userId) => api.post(`/admin/users/${userId}/reset-abuse-score`, {}, { timeout: 10000 }),
  addNote: (data) => api.post('/admin/users/note', data, { timeout: 10000 }),
  
  // Reports
  getReports: (params) => api.get('/admin/reports', { params, timeout: 10000 }),
  getReportDetail: (reportId) => api.get(`/admin/reports/${reportId}`, { timeout: 10000 }),
  moderateReport: (data) => api.post('/admin/reports/moderate', data, { timeout: 10000 }),
  
  // Alerts
  getAlerts: (params) => api.get('/admin/alerts', { params, timeout: 10000 }),
  markAlertReviewed: (data) => api.post('/admin/alerts/review', data, { timeout: 10000 }),
  
  // Audit Log
  getAuditLog: (params) => api.get('/admin/audit-log', { params, timeout: 10000 }),
  
  // Referrals
  getReferrals: (params) => api.get('/admin/referrals', { params, timeout: 10000 }),
  getReferralLeaderboard: () => api.get('/admin/referral-leaderboard', { timeout: 10000 }),
};

// Weather APIs
export const weatherAPI = {
  getCurrent: (lat, lon) => api.get('/weather/current', { params: { lat, lon } }),
  getAlerts: (lat, lon) => api.get('/weather/alerts', { params: { lat, lon } }),
  getForecast: (lat, lon, days = 3) => api.get('/weather/forecast', { params: { lat, lon, days } }),
  getTides: (lat, lon) => api.get('/tides', { params: { lat, lon } }),
};

// Navigation / depth-aware route check (Phase 1) + auto-routing (Phase 2)
export const navigationAPI = {
  depthCheck: (payload) => api.post('/navigation/depth-check', payload),
  route: (payload) => api.post('/navigation/route', payload),
};

// AIS Vessel Traffic APIs (free)
export const aisAPI = {
  getStatus: () => api.get('/ais/status'),
  getVessels: (min_lat, min_lon, max_lat, max_lon) =>
    api.get('/ais/vessels', { params: { min_lat, min_lon, max_lat, max_lon } }),
};

// Marina/Business APIs
export const marinaAPI = {
  getNearby: (lat, lon, radius_nm = 25, business_type = null) => 
    api.get('/marinas/nearby', { params: { lat, lon, radius_nm, business_type } }),
  getDetail: (listingId) => api.get(`/marinas/${listingId}`),
  recordTap: (listingId) => api.post(`/marinas/${listingId}/tap`),
  create: (data) => api.post('/marinas', data),
  update: (listingId, data) => api.put(`/marinas/${listingId}`, data),
  delete: (listingId) => api.delete(`/marinas/${listingId}`),
};

export default api;
